import React, { useState } from "react";
import { useIntl } from "react-intl";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "components/ui/button";
import { Dialog, DialogDescription, DialogTitle } from "components/ui/dialog";
import { DialogCard } from "components/ui/dialog-card";
import bookingValidationData, { BookingValidationStatus } from "hooks/bookingValidation";
import dataFetcher from "hooks/bookService";
import { onlineSalaryDeclaration } from "hooks/cesu";
import { getServerMessage } from "lib/serverError";
import type { BookingInterface } from "types/booking";

type FinalizeAndDeclareDialogProps = {
    booking: BookingInterface;
    open: boolean;
    onOpenChange: (open: boolean) => void;
};

const FinalizeAndDeclareDialog: React.FC<FinalizeAndDeclareDialogProps> = ({ booking, open, onOpenChange }) => {
    const intl = useIntl();
    const queryClient = useQueryClient();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleValidate = async () => {
        try {
            setIsSubmitting(true);

            await onlineSalaryDeclaration(booking.id);
            await bookingValidationData.create({
                booking_id: booking.id,
                status: BookingValidationStatus.VALIDATED,
                duration: booking.nb_hours,
            });
            await dataFetcher.capturePayment({ booking_id: booking.id });

            toast.success(intl.formatMessage({ id: "our_services.booking.confirm.message.transmitting_cesu.success" }));
            queryClient.invalidateQueries({ queryKey: ["profile", "service-history"] });
            onOpenChange(false);
        } catch (err: unknown) {
            toast.error(
                getServerMessage(err)
                    ?? intl.formatMessage({ id: "our_services.booking.confirm.message.transmitting_cesu.error" }),
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogCard closeLabel={intl.formatMessage({ id: "profile.service_history.finalize_modal.close" })}>
                <div className="flex flex-col gap-6">
                    <div>
                        <DialogTitle className="text-xl font-bold text-slate-900">
                            {intl.formatMessage({ id: "profile.service_history.finalize_modal.title" })}
                        </DialogTitle>
                        <DialogDescription className="mt-2 text-sm text-slate-500">
                            {intl.formatMessage({ id: "profile.service_history.finalize_modal.description" })}
                        </DialogDescription>
                        <p className="mt-3 text-sm text-slate-500">
                            {intl.formatMessage({ id: "profile.service_history.finalize_modal.auto_validate_hint" })}
                        </p>
                    </div>

                    <Button
                        type="button"
                        onClick={() => void handleValidate()}
                        disabled={isSubmitting}
                        className="h-12 w-full rounded-xl text-base font-bold"
                    >
                        {intl.formatMessage({ id: "profile.service_history.finalize_modal.confirm" })}
                    </Button>
                </div>
            </DialogCard>
        </Dialog>
    );
};

export default FinalizeAndDeclareDialog;

